/**
 * «Qué hacer en la consola de Twilio» (SPEC-estadisticas-conexion §A.7): la guía paso a paso, en
 * el orden en que hay que hacerlo. Cada paso se marca con un check verde cuando su paso del
 * diagnóstico está en ok (`guiaConEstado`): la guía no sigue pidiendo algo que ya anda.
 *
 * Colapsada por defecto cuando todo lo obligatorio ya está hecho; abierta si falta algo.
 */

import { useEffect, useState } from "react";
import { BookOpen, CheckCircle2, ChevronDown, ChevronRight, Circle, ExternalLink } from "lucide-react";
import type { Diagnostico } from "../../types";
import { guiaConEstado } from "../../logic/guiaConsola";
import { Tarjeta } from "../Tarjeta";

export function GuiaConsola({ diagnostico }: { diagnostico: Diagnostico | null }) {
  const pasos = guiaConEstado(diagnostico);
  const obligatorios = pasos.filter((p) => !p.opcional && p.pasoDiagnostico);
  const hechos = obligatorios.filter((p) => p.hecho).length;
  const completa = obligatorios.length > 0 && hechos === obligatorios.length;
  const [abierta, setAbierta] = useState(!completa);
  const [abiertoPaso, setAbiertoPaso] = useState<string | null>(null);
  // Cuando llega el diagnóstico y todo está hecho, se pliega sola (el operador la puede abrir).
  useEffect(() => { if (completa) setAbierta(false); }, [completa]);

  return (
    <Tarjeta titulo="Qué hacer en la consola de Twilio" sub={`${hechos} de ${obligatorios.length} pasos obligatorios hechos`}
      right={
        <button type="button" onClick={() => setAbierta((v) => !v)} aria-expanded={abierta}
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900">
          <BookOpen size={15} /> {abierta ? "Ocultar" : "Ver la guía"}
        </button>
      }>
      {abierta && (
        <ol className="space-y-2">
          {pasos.map((p, i) => {
            const expandido = abiertoPaso === p.id;
            return (
              <li key={p.id} className="rounded-xl border border-slate-100">
                <button type="button" onClick={() => setAbiertoPaso(expandido ? null : p.id)} aria-expanded={expandido}
                  className="w-full flex items-start gap-2.5 px-4 py-2.5 text-left hover:bg-slate-50 rounded-xl">
                  <span className={`mt-0.5 flex-shrink-0 ${p.hecho ? "text-emerald-600" : "text-slate-300"}`}>
                    {p.hecho ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                    <span className="sr-only">{p.hecho ? "Hecho" : "Pendiente"}</span>
                  </span>
                  <span className="min-w-0 flex-1 text-sm">
                    <span className="font-medium text-slate-800">{i + 1}. {p.titulo}</span>
                    {!expandido && <span className="block text-xs text-slate-500 truncate">{p.porQue}</span>}
                  </span>
                  {expandido ? <ChevronDown size={15} className="mt-0.5 text-slate-400" /> : <ChevronRight size={15} className="mt-0.5 text-slate-400" />}
                </button>
                {expandido && (
                  <div className="px-4 pb-3 pl-11 space-y-2 text-sm">
                    <p className="text-slate-600">{p.porQue}</p>
                    <ul className="list-disc pl-5 space-y-1 text-slate-700">
                      {p.pasos.map((t) => <li key={t}>{t}</li>)}
                    </ul>
                    <div className="flex items-center gap-4 flex-wrap text-xs">
                      <a href={p.enlace} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-violet-700 hover:underline">
                        Abrir la consola de Twilio <ExternalLink size={12} />
                      </a>
                      <a href={p.fuente} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-slate-500 hover:underline">
                        Documentación de Twilio <ExternalLink size={12} />
                      </a>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </Tarjeta>
  );
}

export default GuiaConsola;
